/**
 * 格式調整器：規則定義
 *
 * 所有格式化規則以 plain object 定義，每條規則包含：
 * - id: 唯一識別碼（kebab-case）
 * - name: UI 顯示名稱
 * - category: 分類 key（用於 toggle UI 分組）
 * - phase: 執行階段（數字越小越先執行）
 * - enabledByDefault: 預設開關狀態
 * - apply: 純函式，接收文字回傳轉換後文字
 * - example: （選填）UI 顯示的轉換範例，格式為 'before → after'
 *
 * === Phase 對照表 ===
 *
 * | Phase | 名稱             | 用途                                       |
 * |-------|------------------|--------------------------------------------|
 * | 0     | Char Conversion  | 字元級轉換（簡體→繁體）                      |
 * | 1     | Newline          | 換行處理                                    |
 * | 2     | Word Replacement | 詞彙替換                                    |
 * | 3     | Symbol           | 符號轉換（~→-、->→→、=>→⇒）               |
 * | 4     | Punctuation      | 標點轉換                                    |
 * | 5     | Spacing          | 間距處理                                    |
 * | 6     | Post-fix         | 特殊修復（alwaysEnabled，不可由使用者停用）     |
 * | 7     | Sentence Ending  | 句尾處理                                    |
 *
 * === Phase 之間的因果關係 ===
 *
 * Phase 0 → Phase 2：
 *   簡繁轉換必須在詞彙替換前完成。Phase 2 的「中國→台灣用語」對照表用繁體字定義
 *   （如「數據→資料」），若輸入是簡體的「数据」，Phase 2 無法匹配。
 *   先轉繁體（数据→數據），Phase 2 才能正確替換（數據→資料）。
 *
 * Phase 2 → Phase 4：
 *   詞彙替換必須在標點轉換前完成，避免替換結果中的半形標點被意外轉成全形。
 *
 * Phase 3 → Phase 5：
 *   符號轉換（~→-、->→→）必須在間距處理前完成，
 *   才能讓 spacing-digit-range 對轉換後的符號正確加空白。
 *   例：`3~5` → Phase 3 → `3-5` → Phase 5 → `3 - 5`。
 *
 * Phase 4 → Phase 5：
 *   全形標點需先到位，間距規則才能正確移除標點周圍多餘空白。
 *   例：先把 `,` 轉成 `，`，再移除 `，` 前後空白。
 *
 * Phase 4 → Phase 6：
 *   Phase 4 會把 `1,000` 的逗號轉成全形 `1，000`，Phase 6 修復回來。
 *   此為已知副作用，透過 post-fix 階段統一修復，比在 Phase 4 加入複雜排除邏輯更清晰。
 *
 * Phase 6 → Phase 7：
 *   所有修復完成後，才在句尾加標點，避免修復步驟影響句尾判斷。
 *
 * === alwaysEnabled 屬性 ===
 *
 * 設為 true 的規則不受使用者 toggle 控制，pipeline 執行時總是納入。
 * 目前用於 post-fix 規則——這些規則修復標點轉換的副作用，停用會產生錯誤結果。
 *
 * 同 phase 內的規則彼此獨立，但陣列定義順序會被保留。
 */

import { getConverter } from './opencc_loader'

// === Phase 0: 字元級轉換 (char-conversion) ===

const charS2tw = {
  id: 'char-s2tw',
  name: '簡體轉繁體',
  category: 'char-conversion',
  phase: 0,
  enabledByDefault: false,
  apply: (text) => {
    // converter 由 opencc_loader 非同步載入，未載入時原文回傳（graceful degradation）
    const converter = getConverter()
    if (!converter) return text
    return converter(text)
  }
}

// === Phase 1: 換行處理 (newline) ===

const newlineRemove = {
  id: 'newline-remove',
  name: '移除所有換行',
  category: 'newline',
  phase: 1,
  enabledByDefault: false,
  apply: (text) => {
    const cjk = /[\u2e80-\u2fff\u3004-\u303f\u3040-\u309f\u30a0-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff\ufe30-\ufe4f]/
    return text.replace(/\n+/g, (match, offset, str) => {
      const before = str[offset - 1]
      const after = str[offset + match.length]
      // CJK-CJK：直接刪除（中文不需要空白分隔）
      // 其他組合：替換為空白（保留拉丁文字的 word boundary）
      if (before && after && cjk.test(before) && cjk.test(after)) {
        return ''
      }
      return ' '
    })
  }
}

// === Phase 2: 詞彙替換 (word-replacement) ===

/**
 * 中國用語 → 台灣用語對照表。
 * 使用 Map 保證迭代順序（長詞優先），避免短詞先匹配導致部分替換。
 * 例：「信息化」應整體替換為「資訊化」，而非「信息」先被替換後剩下「資訊化」碰巧正確。
 */
const CHINA_TO_TAIWAN_MAP = new Map([
  // 三字以上優先
  ['人工智能', '人工智慧'],
  ['知識產權', '智慧財產權'],
  ['軟件工程', '軟體工程'],
  ['硬件設備', '硬體設備'],
  ['網絡安全', '網路安全'],
  ['移動支付', '行動支付'],
  ['移動互聯網', '行動網路'],
  ['移動端', '行動裝置端'],
  ['互聯網', '網路'],
  ['物聯網', '物聯網'],  // 相同，但避免「物聯網」被「聯網→連網」影響
  ['服務器', '伺服器'],
  ['操作系統', '作業系統'],
  ['打印機', '印表機'],
  ['內存', '記憶體'],
  ['芯片', '晶片'],
  ['優盤', '隨身碟'],
  ['激光', '雷射'],
  ['數據庫', '資料庫'],
  ['數據', '資料'],
  ['信息', '資訊'],
  ['軟件', '軟體'],
  ['硬件', '硬體'],
  ['網絡', '網路'],
  ['視頻', '影片'],
  ['音頻', '音訊'],
  ['質量', '品質'],
  ['用戶', '使用者'],
  ['默認', '預設'],
  ['支持', '支援'],
  ['高清', '高畫質'],
  ['宏觀', '總體'],
  ['微觀', '個體'],
  ['上市公司', '上市公司'],  // 相同，佔位防止「公司」被其他規則影響
  ['公司', '公司'],
  ['個股', '個股'],
])

const wordChinaToTaiwan = {
  id: 'word-china-to-taiwan',
  name: '中國用語 → 台灣用語',
  category: 'word-replacement',
  phase: 2,
  enabledByDefault: true,
  apply: (text) => {
    let result = text
    for (const [china, taiwan] of CHINA_TO_TAIWAN_MAP) {
      if (china === taiwan) continue  // 跳過佔位項目
      result = result.split(china).join(taiwan)
    }
    return result
  }
}

const wordWifiNormalize = {
  id: 'word-wifi-normalize',
  name: 'wifi／WIFI → Wi-Fi',
  category: 'word-replacement',
  phase: 2,
  enabledByDefault: true,
  apply: (text) => text.replace(/\bwi-?fi\b/gi, 'Wi-Fi')
}

/**
 * 金融常用大寫縮寫修正。
 * 使用 `(?<![a-zA-Z])` / `(?![a-zA-Z])` 而非 `\b`，
 * 因為 `\b` 視數字為 word char，會導致 "eps1.23" 中的 "eps" 不被匹配。
 * 金融文本中術語常緊接數字（EPS1.23、PE10），需要能正確捕捉。
 */
const UPPERCASE_TERMS = ['EPS', 'AI', 'PE', 'PB']

const wordUppercaseTerms = {
  id: 'word-uppercase-terms',
  name: 'EPS／AI／PE／PB 全大寫',
  category: 'word-replacement',
  phase: 2,
  enabledByDefault: true,
  apply: (text) => {
    let result = text
    for (const term of UPPERCASE_TERMS) {
      result = result.replace(
        new RegExp(`(?<![a-zA-Z])${term}(?![a-zA-Z])`, 'gi'),
        term
      )
    }
    return result
  }
}

/** TSMC 先進封裝技術名詞，大小寫混合有固定寫法 */
const TSMC_PACKAGING_TERMS = ['CoWoS', 'CoPoS', 'CoWoP']

const wordTsmcPackaging = {
  id: 'word-tsmc-packaging',
  name: 'CoWoS／CoPoS／CoWoP',
  category: 'word-replacement',
  phase: 2,
  enabledByDefault: true,
  apply: (text) => {
    let result = text
    for (const term of TSMC_PACKAGING_TERMS) {
      result = result.replace(
        new RegExp(`(?<![a-zA-Z])${term}(?![a-zA-Z])`, 'gi'),
        term
      )
    }
    return result
  }
}

// === Phase 3: 符號轉換 (symbol) ===

const symbolTildeToHyphen = {
  id: 'symbol-tilde-to-hyphen',
  name: '~ → -（數字間）',
  example: '3~5% → 3-5%',
  category: 'symbol',
  phase: 3,
  enabledByDefault: true,
  apply: (text) => text.replace(/(?<=\d)~(?=\d)/g, '-')
}

const symbolArrowSingle = {
  id: 'symbol-arrow-single',
  name: '-> → →',
  example: '-> → →',
  category: 'symbol',
  phase: 3,
  enabledByDefault: true,
  apply: (text) => text.replace(/-+>/g, '→')
}

const symbolArrowDouble = {
  id: 'symbol-arrow-double',
  name: '=> → ⇒',
  example: '=> → ⇒',
  category: 'symbol',
  phase: 3,
  enabledByDefault: true,
  apply: (text) => text.replace(/=+>/g, '⇒')
}

// === Phase 4: 標點轉換 (punctuation) ===

const punctHalfToFullSlash = {
  id: 'punct-half-to-full-slash',
  name: '/ → ／',
  category: 'punctuation',
  phase: 4,
  enabledByDefault: true,
  apply: (text) => {
    // 先將 URL 暫時替換為 placeholder，避免其中的斜線被轉換
    const urlPlaceholders = []
    let processed = text.replace(/https?:\/\/\S+/g, (url) => {
      urlPlaceholders.push(url)
      return `\x00URL${urlPlaceholders.length - 1}\x00`
    })
    // 轉換非 URL 內的斜線
    processed = processed.replace(/\//g, '／')
    // 還原 URL
    processed = processed.replace(/\x00URL(\d+)\x00/g, (_, i) => urlPlaceholders[parseInt(i, 10)])
    return processed
  }
}

const punctHalfToFullParens = {
  id: 'punct-half-to-full-parens',
  name: '() → （）',
  category: 'punctuation',
  phase: 4,
  enabledByDefault: false,
  apply: (text) => text.replace(/\(/g, '（').replace(/\)/g, '）')
}

const punctFullToHalfParens = {
  id: 'punct-full-to-half-parens',
  name: '（）→ ()',
  category: 'punctuation',
  phase: 4,
  enabledByDefault: true,
  apply: (text) => {
    let result = text.replace(/（/g, '(').replace(/）/g, ')')
    // 非空白字元後接 ( → 加空白（Phase 5 會清理全形標點前的多餘空白）
    result = result.replace(/(\S)\(/g, '$1 (')
    // ) 後接非空白字元 → 加空白（Phase 5 會清理全形標點後的多餘空白）
    result = result.replace(/\)(\S)/g, ') $1')
    return result
  }
}

const punctHalfToFullComma = {
  id: 'punct-half-to-full-comma',
  name: ', → ，',
  category: 'punctuation',
  phase: 4,
  enabledByDefault: true,
  apply: (text) => {
    // 排除數字中的逗號（如 1,000）——但此處先全部轉換，Phase 6 的 fix-number-comma 會修復回來
    // 這樣設計是因為判斷「哪些逗號在數字中」的邊界案例很多，集中在 post-fix 處理更可靠
    return text.replace(/,/g, '，')
  }
}

const punctHalfToFullColon = {
  id: 'punct-half-to-full-colon',
  name: ': → ：',
  category: 'punctuation',
  phase: 4,
  enabledByDefault: true,
  apply: (text) => {
    // 排除 URL 中的冒號（http: https:）和時間格式（12:30）
    return text.replace(/(?<!\w):(?!\d{2}(?::\d{2})?(?!\d))(?!\/\/)/g, '：')
  }
}

const punctHalfToFullSemicolon = {
  id: 'punct-half-to-full-semicolon',
  name: '; → ；',
  category: 'punctuation',
  phase: 4,
  enabledByDefault: true,
  apply: (text) => text.replace(/;/g, '；')
}

const punctFullToHalfPercent = {
  id: 'punct-full-to-half-percent',
  name: '％ → %',
  category: 'punctuation',
  phase: 4,
  enabledByDefault: true,
  apply: (text) => text.replace(/％/g, '%')
}

// === Phase 5: 間距處理 (spacing) ===

const spacingCjkLatin = {
  id: 'spacing-cjk-latin',
  name: '中文與英數之間加空白',
  category: 'spacing',
  phase: 5,
  enabledByDefault: true,
  apply: (text) => {
    // CJK 字元範圍（涵蓋中日韓統一漢字、注音、假名等）
    const cjk = '\\u2e80-\\u2fff\\u3004-\\u303f\\u3040-\\u309f\\u30a0-\\u30ff\\u3400-\\u4dbf\\u4e00-\\u9fff\\uf900-\\ufaff\\ufe30-\\ufe4f'
    // CJK 後接英文/數字：加空白
    let result = text.replace(new RegExp(`([${cjk}])([A-Za-z0-9])`, 'g'), '$1 $2')
    // 英文/數字後接 CJK：加空白
    result = result.replace(new RegExp(`([A-Za-z0-9])([${cjk}])`, 'g'), '$1 $2')
    return result
  }
}

const spacingRemoveAroundPunct = {
  id: 'spacing-remove-around-punct',
  name: '移除全形標點前後空白',
  category: 'spacing',
  phase: 5,
  enabledByDefault: true,
  apply: (text) => {
    // 全形標點符號（逗號、句號、頓號、分號、冒號、問號、驚嘆號、括號等）
    const fullWidthPunct = '，。、；：？！「」『』（）【】〈〉《》〔〕'
    // 移除全形標點前的空白
    let result = text.replace(new RegExp(`\\s+([${fullWidthPunct}])`, 'g'), '$1')
    // 移除全形標點後的空白（但不移除換行）
    result = result.replace(new RegExp(`([${fullWidthPunct}])[ \\t]+`, 'g'), '$1')
    return result
  }
}

const spacingDigitRange = {
  id: 'spacing-digit-range',
  name: '數字與範圍符號間加空白',
  category: 'spacing',
  phase: 5,
  enabledByDefault: true,
  apply: (text) => {
    // 保護 ISO 日期格式（YYYY-MM-DD、YYYY-MM），避免被加空白
    const datePlaceholders = []
    let processed = text.replace(/\d{4}-\d{2}(?:-\d{2})?(?!\d)/g, (date) => {
      datePlaceholders.push(date)
      return `\x00DATE${datePlaceholders.length - 1}\x00`
    })
    // 數字與範圍符號（-、→、⇒）之間加空白，同時正規化不一致的間距
    processed = processed.replace(/(?<=\d)\s*-\s*(?=\d)/g, ' - ')
    processed = processed.replace(/(?<=\d)\s*→\s*(?=\d)/g, ' → ')
    processed = processed.replace(/(?<=\d)\s*⇒\s*(?=\d)/g, ' ⇒ ')
    // 還原日期
    processed = processed.replace(/\x00DATE(\d+)\x00/g, (_, i) => datePlaceholders[parseInt(i, 10)])
    return processed
  }
}

// === Phase 6: 特殊修復 (post-fix) ===

const fixNumberComma = {
  id: 'fix-number-comma',
  name: '修復數字中的全形逗號',
  example: '1，000 → 1,000',
  category: 'post-fix',
  phase: 6,
  enabledByDefault: true,
  alwaysEnabled: true,
  apply: (text) => {
    // 將數字中被誤轉的全形逗號修復回半形：1，000，000 → 1,000,000
    // 需重複執行直到沒有變化，因為連續千分位（如 1，000，000）的第二個逗號
    // 在第一次替換後才符合「數字 + 全形逗號 + 三位數字」的模式
    let result = text
    let prev
    do {
      prev = result
      result = result.replace(/(\d)，(\d{3})/g, '$1,$2')
    } while (result !== prev)
    return result
  }
}

const fixEpsSpacing = {
  id: 'fix-eps-spacing',
  name: 'EPS 與數字間加空白',
  example: 'EPS1.23 → EPS 1.23',
  category: 'post-fix',
  phase: 6,
  enabledByDefault: true,
  alwaysEnabled: true,
  apply: (text) => {
    // EPS1.23 → EPS 1.23（EPS 後緊接數字時加空白）
    return text.replace(/EPS([-\d])/g, 'EPS $1')
  }
}

const fixPercentCjkSpacing = {
  id: 'fix-percent-cjk-spacing',
  name: '% 後接 CJK 加空白',
  example: '20%成長 → 20% 成長',
  category: 'post-fix',
  phase: 6,
  enabledByDefault: true,
  alwaysEnabled: true,
  apply: (text) => {
    const cjk = '\\u2e80-\\u2fff\\u3004-\\u303f\\u3040-\\u309f\\u30a0-\\u30ff\\u3400-\\u4dbf\\u4e00-\\u9fff\\uf900-\\ufaff\\ufe30-\\ufe4f'
    return text.replace(new RegExp(`%([${cjk}])`, 'g'), '% $1')
  }
}

const fixNegativePercentSpacing = {
  id: 'fix-negative-percent-spacing',
  name: '負百分比前加空白',
  example: '衰退-4% → 衰退 -4%',
  category: 'post-fix',
  phase: 6,
  enabledByDefault: true,
  alwaysEnabled: true,
  apply: (text) => {
    const cjk = '\\u2e80-\\u2fff\\u3004-\\u303f\\u3040-\\u309f\\u30a0-\\u30ff\\u3400-\\u4dbf\\u4e00-\\u9fff\\uf900-\\ufaff\\ufe30-\\ufe4f'
    // CJK 字元後緊接負號和數字：加空白
    return text.replace(new RegExp(`([${cjk}])(-)(\\d+\\.?\\d*%)`, 'g'), '$1 $2$3')
  }
}

// === Phase 7: 句尾處理 (sentence-ending) ===

const sentenceAppendPeriod = {
  id: 'sentence-append-period',
  name: '結尾自動加句號',
  category: 'sentence-ending',
  phase: 7,
  enabledByDefault: true,
  apply: (text) => {
    // 若最後一個非空白字元不是標點符號，則加上「。」
    const trimmed = text.trimEnd()
    if (!trimmed) return text
    const lastChar = trimmed[trimmed.length - 1]
    const endPuncts = '。，、；：？！」』）】〉》.!?'
    if (endPuncts.includes(lastChar)) return text
    return trimmed + '。'
  }
}

// === 匯出：規則陣列（定義順序即同 phase 內的執行順序） ===

export const rules = [
  // Phase 0: 字元轉換
  charS2tw,
  // Phase 1: 換行
  newlineRemove,
  // Phase 2: 詞彙替換
  wordChinaToTaiwan,
  wordWifiNormalize,
  wordUppercaseTerms,
  wordTsmcPackaging,
  // Phase 3: 符號
  symbolTildeToHyphen,
  symbolArrowSingle,
  symbolArrowDouble,
  // Phase 4: 標點
  punctHalfToFullSlash,
  punctHalfToFullParens,
  punctFullToHalfParens,
  punctHalfToFullComma,
  punctHalfToFullColon,
  punctHalfToFullSemicolon,
  punctFullToHalfPercent,
  // Phase 5: 間距
  spacingCjkLatin,
  spacingRemoveAroundPunct,
  spacingDigitRange,
  // Phase 6: 特殊修復
  fixNumberComma,
  fixEpsSpacing,
  fixPercentCjkSpacing,
  fixNegativePercentSpacing,
  // Phase 7: 句尾
  sentenceAppendPeriod,
]

/**
 * 分類定義，用於 UI 分組顯示。
 *
 * uiMode 決定該分類在 toggle UI 中的呈現方式：
 * - 'category': 只顯示類別開關，不展開個別規則（規則總是一起啟用/停用）
 * - 'individual': 展開個別規則開關，使用者可細粒度控制
 * - 'radio': 互斥選擇（如換行處理只能選一種）
 * - 'single': 單一 toggle（如句尾加句號）
 */
export const categories = [
  { key: 'char-conversion', name: '簡體轉繁體', uiMode: 'single', phase: 0 },
  { key: 'symbol', name: '符號轉換', uiMode: 'individual', phase: 3 },
  { key: 'punctuation', name: '標點符號半形轉全形', uiMode: 'individual', phase: 4 },
  { key: 'spacing', name: '間距處理', uiMode: 'individual', phase: 5 },
  { key: 'word-replacement', name: '詞彙替換', uiMode: 'individual', phase: 2 },
  { key: 'newline', name: '移除所有換行', uiMode: 'single', phase: 1 },
  { key: 'sentence-ending', name: '句尾加句號', uiMode: 'single', phase: 7 },
]

/** 取得特定分類下的所有規則 */
export function getRulesByCategory(categoryKey) {
  return rules.filter(r => r.category === categoryKey)
}

/**
 * 取得中國→台灣用語替換對照表（排除佔位項目）。
 *
 * 用於 UI 顯示替換清單，讓使用者知道具體會替換哪些詞彙。
 *
 * @returns {Array<[string, string]>} [中國用語, 台灣用語] 的陣列
 */
export function getWordReplacementPairs() {
  return [...CHINA_TO_TAIWAN_MAP].filter(([china, taiwan]) => china !== taiwan)
}

/**
 * 取得特定規則的資料。
 *
 * @param {string} ruleId - 規則 ID
 * @returns {Object|undefined} 規則物件
 */
export function getRuleById(ruleId) {
  return rules.find(r => r.id === ruleId)
}
